/**
 * AdminPage - Admin panel for batch project generation
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  TextField,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  Divider,
  Chip,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { API_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

interface AdminPageProps {
  onBack: () => void;
}

interface BatchStatus {
  status: 'idle' | 'running' | 'completed' | 'error';
  message?: string;
  batch_id?: string;
  result?: {
    total: number;
    successful: number;
    failed: number;
    success_rate: number;
  };
}

const AdminPage: React.FC<AdminPageProps> = ({ onBack }) => {
  const { token, user } = useAuth();
  const [testMode, setTestMode] = useState(true);
  const pollingIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  
  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);
  
  const pollBatchStatus = async (batchId: string) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    
    let pollCount = 0;
    const maxPolls = 360; // Max 30 minutes (360 * 5 seconds)
    
    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      
      // Stop polling after max attempts (safety measure)
      if (pollCount > maxPolls) {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        setBatchStatus({
          status: 'error',
          message: 'Batch generation status check timed out. Please check server logs.',
          batch_id: batchId
        });
        return;
      }
      
      try {
        const response = await fetch(`${API_URL}/api/admin/batch-status/${batchId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          // If 503 (service unavailable), continue polling (database might be reconnecting)
          if (response.status === 503) {
            console.log('Database temporarily unavailable, continuing to poll...');
            return;
          }
          // For other errors, log but continue polling
          console.error('Failed to get batch status:', response.status);
          return;
        }
        
        const status = await response.json();
        
        if (status.status === 'completed') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setBatchStatus({
            status: 'completed',
            message: 'Batch generation completed successfully!',
            batch_id: batchId,
            result: status.result
          });
        } else if (status.status === 'error') {
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setBatchStatus({
            status: 'error',
            message: status.error || 'Batch generation failed',
            batch_id: batchId
          });
        } else if (status.status === 'not_found') {
          // Batch ID not found - might have been cleared or server restarted
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setBatchStatus({
            status: 'error',
            message: 'Batch generation status not found. The server may have restarted. Please check server logs.',
            batch_id: batchId
          });
        }
        // If still running, continue polling
      } catch (error) {
        // Network errors - continue polling (might be temporary)
        console.error('Error polling batch status:', error);
        // Don't stop polling on network errors, they might be temporary
      }
    }, 5000); // Poll every 5 seconds
  };
  const [limit, setLimit] = useState<number | null>(null);
  const [batchStatus, setBatchStatus] = useState<BatchStatus>({ status: 'idle' });
  const [loading, setLoading] = useState(false);
  const [templatesCount, setTemplatesCount] = useState<number | null>(null);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Admin users list
  const ADMIN_EMAILS = [
    'ingmarinuzzi@gmail.com',
    'alessandro@marinuzzi.com',
    'al.marinuzzi@gmail.com'
  ];
  
  const isAdminUser = (email: string | undefined): boolean => {
    return email !== undefined && ADMIN_EMAILS.includes(email);
  };
  
  // Check if user is admin
  const isAdmin = isAdminUser(user?.email);

  React.useEffect(() => {
    loadTemplatesCount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadTemplatesCount = async () => {
    if (!token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/templates-count`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTemplatesCount(data.count);
      }
    } catch (error) {
      console.error('Error loading templates count:', error);
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      alert('Please select a JSON file (.json)');
      return;
    }

    if (!token) {
      alert('Not authenticated. Please login again.');
      return;
    }

    setLoadingTemplates(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/load-templates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to load templates');
      }

      const data = await response.json();
      alert(
        `✅ Templates loaded successfully!\n\n` +
        `Loaded: ${data.loaded}\n` +
        `Skipped: ${data.skipped}\n` +
        `Total in file: ${data.total_in_file}\n` +
        `Total in database: ${data.new_count}`
      );
      await loadTemplatesCount();
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
    } catch (err: any) {
      alert(`Error loading templates: ${err.message}`);
    } finally {
      setLoadingTemplates(false);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)', py: 4 }}>
        <Container maxWidth="lg">
          <Paper elevation={8} sx={{ p: 4, background: 'rgba(255, 255, 255, 0.98)', borderRadius: 4 }}>
            <Alert severity="error" sx={{ mb: 3 }}>
              Access denied. Admin privileges required.
            </Alert>
            <Button variant="contained" startIcon={<BackIcon />} onClick={onBack}>
              Back
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  const handleStartBatch = async () => {
    if (!token) {
      setBatchStatus({
        status: 'error',
        message: 'Not authenticated. Please login again.'
      });
      return;
    }

    setLoading(true);
      setBatchStatus({ status: 'running', message: 'Batch generation started...', batch_id: undefined });

    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (testMode) {
        params.append('test_mode', 'true');
      }
      if (limit && !testMode) {
        params.append('limit', limit.toString());
      }

      const url = `${API_URL}/api/admin/batch-generate?${params.toString()}`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to start batch generation');
      }

      const data = await response.json();
      
      const batchId = data.batch_id;
      setBatchStatus({
        status: 'running',
        message: data.message || 'Batch generation started in background. Check server logs for progress.',
        batch_id: batchId
      });
      
      // Start polling for status updates if we have a batch_id
      if (batchId) {
        pollBatchStatus(batchId);
      }
      
    } catch (err: any) {
      setBatchStatus({
        status: 'error',
        message: err.message || 'Failed to start batch generation'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)', py: 4 }}>
      <Container maxWidth="lg">
        <Paper elevation={8} sx={{ p: 4, background: 'rgba(255, 255, 255, 0.98)', borderRadius: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <Button
              startIcon={<BackIcon />}
              onClick={onBack}
              sx={{ mr: 2 }}
            >
              Back
            </Button>
            <SettingsIcon sx={{ fontSize: 32, color: '#003399', mr: 2 }} />
            <Typography variant="h4" component="h1" sx={{ flexGrow: 1, color: '#003399', fontWeight: 700 }}>
              Admin Panel
            </Typography>
            <Chip
              label="ADMIN"
              color="error"
              sx={{ fontWeight: 700 }}
            />
          </Box>

          <Divider sx={{ mb: 4 }} />

          {/* Templates Management Section */}
          <Card sx={{ mb: 4, border: '2px solid', borderColor: 'info.main' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: '#003399', fontWeight: 600, mb: 3 }}>
                📚 Project Templates Management
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                Load the 1000 project templates from the <code>erasmus_1000_cases.json</code> file into the database.
                This needs to be done <strong>once before running batch generation</strong>.
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>How it works:</strong> The JSON file contains 1000 project cases with Step 2 fields (problem statement, objectives, etc.).
                  After loading, the batch generator will read from the database instead of the file system.
                </Typography>
              </Alert>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Templates in database:</strong> {templatesCount !== null ? templatesCount : 'Loading...'}
                </Typography>
                
                {templatesCount === 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    No templates found in database. Please load templates from JSON file first.
                  </Alert>
                )}
              </Box>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept=".json"
                style={{ display: 'none' }}
              />
              
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={loadingTemplates ? <CircularProgress size={20} color="inherit" /> : <SettingsIcon />}
                  onClick={handleFileSelect}
                  disabled={loadingTemplates || templatesCount === null}
                  sx={{
                    px: 4,
                    py: 1.5
                  }}
                >
                  {loadingTemplates ? 'Loading Templates...' : 'Upload JSON File'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={loadTemplatesCount}
                  disabled={loadingTemplates}
                  sx={{ py: 1.5 }}
                >
                  Refresh Count
                </Button>
              </Box>
              
              {templatesCount !== null && templatesCount > 0 && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    ✅ <strong>{templatesCount} templates</strong> are ready in the database. You can now run batch generation.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Batch Generation Section */}
          <Card sx={{ mb: 4, border: '2px solid', borderColor: 'primary.main' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom sx={{ color: '#003399', fontWeight: 600, mb: 3 }}>
                🚀 Batch Project Generation
              </Typography>
              
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                Generate multiple Erasmus+ projects from the templates in the database.
                Projects will be created with fixed coordinator (Oystar Technology, NL, Company)
                and generated Word documents.
              </Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                  ⏱️ Estimated Duration:
                </Typography>
                <Typography variant="body2" component="div">
                  • <strong>1 project (Test Mode):</strong> ~4-6 minutes<br/>
                  • <strong>10 projects:</strong> ~40-60 minutes<br/>
                  • <strong>100 projects:</strong> ~6-10 hours<br/>
                  • <strong>1000 projects:</strong> ~2.5-4 days (66-100 hours)
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                  ✅ <strong>Good news:</strong> The batch generation runs in the background on the server.
                  You can safely close this page - the generation will continue!
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Check the "My Projects" page later to see the generated projects, or check server logs for progress.
                </Typography>
              </Alert>

              {/* Configuration */}
              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={testMode}
                      onChange={(e) => {
                        setTestMode(e.target.checked);
                        if (e.target.checked) {
                          setLimit(null);
                        }
                      }}
                      color="primary"
                    />
                  }
                  label="Test Mode (generate only 1 project)"
                  sx={{ mb: 2, display: 'block' }}
                />

                {!testMode && (
                  <TextField
                    label="Limit (number of projects to generate)"
                    type="number"
                    value={limit || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      setLimit(value ? parseInt(value, 10) : null);
                    }}
                    helperText="Leave empty to generate all 1000 projects"
                    sx={{ mb: 2, width: '100%', maxWidth: 400 }}
                    inputProps={{ min: 1, max: 1000 }}
                  />
                )}
              </Box>

              {/* Status Display */}
              {batchStatus.status !== 'idle' && (
                <Box sx={{ mb: 3 }}>
                  {batchStatus.status === 'running' && (
                    <Alert severity="info" icon={<CircularProgress size={20} />} sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        {batchStatus.message}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        The batch generation is running in the background. Check the server logs for detailed progress.
                        You can monitor progress by checking the "My Projects" page.
                      </Typography>
                      <LinearProgress sx={{ mt: 2 }} />
                    </Alert>
                  )}

                  {batchStatus.status === 'completed' && batchStatus.result && (
                    <Alert severity="success" icon={<CheckIcon />} sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                        Batch generation completed!
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2">
                          Total: {batchStatus.result.total} | 
                          ✅ Successful: {batchStatus.result.successful} | 
                          ❌ Failed: {batchStatus.result.failed}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Success rate: {batchStatus.result.success_rate.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Alert>
                  )}

                  {batchStatus.status === 'error' && (
                    <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                      {batchStatus.message}
                    </Alert>
                  )}
                </Box>
              )}

              {/* Start Button */}
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
                onClick={handleStartBatch}
                disabled={loading || batchStatus.status === 'running'}
                sx={{
                  bgcolor: '#003399',
                  '&:hover': { bgcolor: '#0052cc' },
                  px: 4,
                  py: 1.5
                }}
              >
                {loading ? 'Starting...' : testMode ? 'Generate Test Project' : limit ? `Generate ${limit} Projects` : 'Generate All 1000 Projects'}
              </Button>

              {/* Warning */}
              {!testMode && !limit && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    ⚠️ Warning: Generating all 1000 projects will take approximately 2.5-4 days (66-100 hours)
                  </Typography>
                  <Typography variant="body2">
                    Each project requires AI generation of work packages, 9 sections, and document creation (~4-6 minutes per project).
                    The generation runs in the background - you can close this page and check back later.
                    Consider using a limit (e.g., 10 or 100) or test mode first.
                  </Typography>
                </Alert>
              )}
              
              {!testMode && limit && limit >= 100 && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    ⏱️ Estimated time for {limit} projects: ~{Math.round(limit * 5 / 60)}-{Math.round(limit * 6 / 60)} hours
                  </Typography>
                  <Typography variant="body2">
                    Generation runs in background. You can close this page and check "My Projects" later.
                  </Typography>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Info Card */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ color: '#003399', fontWeight: 600 }}>
                ℹ️ How it works
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                <strong>Test Mode:</strong> Generates only the first project from the JSON file. Use this to verify everything works correctly.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                <strong>Limit:</strong> Generate a specific number of projects (e.g., 10, 50, 100). Useful for gradual processing.
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                <strong>No Limit:</strong> Generate all 1000 projects. This will take many hours to complete.
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                <strong>Note:</strong> The batch generation runs in the background. You can close this page and check the "My Projects" page later to see the generated projects.
              </Typography>
            </CardContent>
          </Card>
        </Paper>
      </Container>
    </Box>
  );
};

export default AdminPage;

